import {
  Alert,
  Button,
  FormRow,
  Input,
  Markdown,
  Modal,
  Spinner,
  Stack
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import Joi from 'joi';
import { joiResolver } from '@hookform/resolvers/joi';
import { useState } from 'react';
import { AxiosResponse } from 'axios';
import {
  DestinationConfig,
  ForwardingErrorDetails,
  forwardingProblemTypes,
  resolveForwardingErrorMessage,
  useOrganizationForwardingCheck,
  useOrganizationForwardingPost,
  useOrganizationForwardingPut
} from '../api/hooks/useOrganizationForwarding';
import { DefaultResponseData } from '../shared/notification/useNotification';

const inlineForwardingErrorOptions = {
  suppressedErrorTypes: forwardingProblemTypes
};

interface CreateAndUpdateFitCoDestinationForm {
  destinationId: string;
  publicServiceIdenitifier: string;
  name: string;
}

interface CreateAndUpdateMandantCredsModalProps {
  readonly onClose: () => void;
  readonly onSuccess?: () => void;
  readonly isOpen: boolean;
  readonly orgUnitId: string;
  readonly destinationId?: string;
  readonly publicServiceIdentifier?: string;
  readonly publicServiceId: string;
  readonly forwardingName?: string;
}

export function CreateAndUpdateFitCoDestinationModal(
  props: CreateAndUpdateMandantCredsModalProps
) {
  const {
    isOpen,
    onClose,
    onSuccess,
    orgUnitId,
    publicServiceId,
    publicServiceIdentifier,
    destinationId,
    forwardingName
  } = props;
  const isUpdatingDestination = !!publicServiceIdentifier;
  const { t } = useTranslation();
  const [forwardingError, setForwardingError] = useState<
    ForwardingErrorDetails | undefined
  >();
  const { mutateAsync: updateOrganizationForwarding, isPending: isUpdating } =
    useOrganizationForwardingPut(inlineForwardingErrorOptions);
  const { mutateAsync: createOrganizationForwarding, isPending: isCreating } =
    useOrganizationForwardingPost(inlineForwardingErrorOptions);
  const { mutateAsync: checkOrganizationForwarding, isPending: isChecking } =
    useOrganizationForwardingCheck(inlineForwardingErrorOptions);

  const handleForwardingError = (error: unknown) => {
    const response = (error as { cause?: AxiosResponse<DefaultResponseData> })
      ?.cause;
    const resolved = resolveForwardingErrorMessage(
      t,
      response?.data?.type,
      response?.data?.reasons
    );
    if (!resolved) {
      return;
    }
    setForwardingError(
      resolved.schemaUris.length > 0
        ? {
            ...resolved,
            message: t('createAndUpdateFitCoDestModal.unsupportedSchemas')
          }
        : resolved
    );
  };

  const buildDestinationConfig = (
    state: CreateAndUpdateFitCoDestinationForm
  ): DestinationConfig => ({
    name: state.name,
    publicServiceIdentifier: publicServiceId,
    forwardingType: 'FITCONNECT',
    forwardingTargetConfig: {
      destinationId: state.destinationId,
      publicServiceIdentifier: state.publicServiceIdenitifier
    }
  });

  const isSaving = isCreating || isUpdating;

  const {
    formState: { isValid },
    register,
    handleSubmit
  } = useForm<CreateAndUpdateFitCoDestinationForm>({
    mode: 'onChange',
    defaultValues: {
      destinationId: destinationId ?? '',
      publicServiceIdenitifier: publicServiceIdentifier ?? '',
      name: forwardingName ?? ''
    },
    resolver: joiResolver(
      Joi.object<CreateAndUpdateFitCoDestinationForm>({
        destinationId: Joi.string().guid().required(),
        publicServiceIdenitifier: Joi.string().required(),
        name: Joi.string().allow('')
      })
    )
  });

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Modal.Header id="CreateAndUpdateFitCoDestinationModal-header">
        {t('createAndUpdateFitCoDestModal.title')}
      </Modal.Header>

      <Modal.Content>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Stack spacing="xl" direction="column">
            <div className="descriptionText">
              {t('createAndUpdateFitCoDestModal.subline')}
            </div>
            <Alert
              variant={'info'}
              message={t('createAndUpdateFitCoDestModal.subline.info')}
            />
            <Stack spacing="s">
              <FormRow
                label={t('createAndUpdateFitCoDestModal.destinationId.label')}
              >
                <Input
                  id="destinationId"
                  readonly={isUpdatingDestination}
                  type="text"
                  {...register('destinationId')}
                  placeholder={t(
                    'createAndUpdateFitCoDestModal.destinationId.placeholder'
                  )}
                />
              </FormRow>
              <FormRow
                label={t('createAndUpdateFitCoDestModal.publicServiceId.label')}
              >
                <Input
                  id="publicServiceIdentifier"
                  readonly={isUpdatingDestination}
                  type="text"
                  {...register('publicServiceIdenitifier')}
                  placeholder={t(
                    'createAndUpdateFitCoDestModal.publicServiceId.placeholder'
                  )}
                />
              </FormRow>
              <FormRow
                label={t('createAndUpdateFitCoDestModal.zustellpunkt.label')}
              >
                <Input
                  placeholder={t(
                    'createAndUpdateFitCoDestModal.zustellpunkt.placeholder'
                  )}
                  id="name"
                  {...register('name')}
                  type="text"
                />
              </FormRow>
            </Stack>
            {
              <Button
                variant="secondary-outline"
                disabled={!isValid || isSaving || isChecking}
                onClick={handleSubmit((state) => {
                  setForwardingError(undefined);
                  checkOrganizationForwarding({
                    orgUnitId,
                    destinationConfig: buildDestinationConfig(state)
                  }).catch(handleForwardingError);
                })}
              >
                {t('createAndUpdateFitCoDestModal.connect.button')}
              </Button>
            }
            {forwardingError && (
              <Alert
                variant="error"
                message={
                  <Markdown style={{ wordBreak: 'break-word' }}>
                    {[
                      forwardingError.message,
                      ...forwardingError.schemaUris.map(
                        (schemaUri) => `* ${schemaUri}`
                      )
                    ].join('\n')}
                  </Markdown>
                }
              />
            )}
          </Stack>
        </form>
      </Modal.Content>
      <Modal.Footer direction="rtl" alignment="none">
        <Button size="sm" variant="secondary-outline" onClick={onClose}>
          {t('globalNotifications.modalAboard')}
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={!isValid || isSaving || isChecking}
          onClick={handleSubmit((state) => {
            setForwardingError(undefined);
            const mutationPromise = destinationId
              ? updateOrganizationForwarding({
                  orgUnitId,
                  destinationConfig: buildDestinationConfig(state)
                })
              : createOrganizationForwarding({
                  orgUnitId,
                  destinationConfig: buildDestinationConfig(state)
                });
            mutationPromise
              .then(() => {
                onSuccess?.();
              })
              .catch(handleForwardingError);
          })}
        >
          {isSaving ? <Spinner /> : t('globalNotifications.modalSave')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
